import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import ChatCard, {
  TicketStatusType,
} from "components/DesignSystem/ChatCard/v2/ChatCard";
import { PAID } from "constants/billing";
import { TICKET_CREATED, TICKET_PAID, TICKET_PAYMENT } from "constants/ticket";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useLazyGetTicketByIdQuery } from "store/apis/chatTicket";
import { BillingInvoice } from "types/Models/billing";
import {
  CardStatusType,
  CustomData,
} from "types/Models/reviewDocumentAndBalancingPayment";
import { BackendError } from "types/utils/error";
import { TicketCardBtn } from "./TicketCardBtn";
import { getTicketCardStatus, TicketCardIcon } from "./TicketCardStatus";
import { ClampedTextWithTooltip } from "utils/ClampedTextWithTooltip";
import classNames from "classnames";
import { PricedTicketStatus } from "./PricedTicketStatus";
import { actionItemsApi } from "store/apis/actionItems";
import { useDispatch } from "react-redux";

type TicketCardProps = {
  customData: CustomData;
  messageId: string;
  entityId: string | undefined;
  groupId: string;
};

export const TicketCard = ({
  customData,
  messageId,
  entityId: entityIdFromProps,
  groupId,
}: TicketCardProps) => {
  const { header, description, card_status, cta_data, metadata } = customData;
  const status: TicketStatusType | CardStatusType =
    card_status || TICKET_CREATED;
  const history = useHistory();
  const paymentModal = useModal();
  const ticketId =
    cta_data?.[0]?.metadata?.ticket_id || cta_data?.[1]?.metadata?.ticket_id;
  const [ticketInvoices, setTicketInvoices] = useState<BillingInvoice[]>([]);
  const [
    getTicketDetails,
    { data: ticketDetails, isFetching: isGettingInvoices },
  ] = useLazyGetTicketByIdQuery();
  const { isCustomer, isForeignCA } = useRoleBasedView();
  const { alertToast } = useToast();
  const isPaymentRequire = cta_data?.some(
    (cta) => cta.document_operation === "PAY_TICKET"
  );
  const dispatch = useDispatch();

  const entityId = ticketDetails?.entity?.uuid ?? entityIdFromProps;

  const openPaymentModal = async () => {
    if (groupId && ticketId) {
      try {
        const data = await getTicketDetails({
          groupId: groupId,
          ticketId: ticketId,
        }).unwrap();
        const invoices =
          data?.ticket_payments
            ?.map((paymentDetail) => paymentDetail.ticket_invoice)
            .filter((invoice) => invoice != null && invoice.status !== PAID) ||
          [];
        const payerEntityId = data?.entity?.uuid ?? entityIdFromProps;
        setTicketInvoices(invoices as BillingInvoice[]);

        if (invoices.length === 0) {
          alertToast({ message: "No due payments." });
          return;
        }

        if (!payerEntityId) {
          alertToast({
            message: "No entity linked to this ticket. Please contact support.",
          });
          return;
        }

        paymentModal.open();
      } catch (e) {
        alertToast({
          message:
            (e as BackendError)?.data?.error?.message ?? "Something went wrong",
        });
      }
    }
  };

  const onInvoicePaid = () => {
    dispatch(
      actionItemsApi.util.invalidateTags([{ type: "ALL_ACTION_ITEMS" }])
    );
    paymentModal.close();
  };

  const redirectToTicket = (ticketId?: string) => {
    if (isCustomer) {
      history.push(`/settings/tickets?ticketUuid=${ticketId}`);
    } else {
      history.push(`/admin/tickets?ticketUuid=${ticketId}`);
    }
  };

  return (
    <div>
      <ChatCard.Content addMargin={false}>
        <ChatCard.Header icon={<TicketCardIcon status={status} />}>
          <div className="t-text-subtext-sm t-text-text-30">
            {getTicketCardStatus(status)}
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div>
            <div className="t-min-w-52 t-text-body-sm t-text-text-30 t-mb-1.5">
              {header}
            </div>
            {description && (
              <ClampedTextWithTooltip
                text={description}
                lineClampElementClass="t-line-clamp-2 t-text-subtext-sm t-text-text-60"
                toolTipClass="t-w-64 t-word-break"
              />
            )}
            {(status === TICKET_PAYMENT || status === TICKET_PAID) && (
              <div className="t-mt-3">
                <PricedTicketStatus status={status} />
              </div>
            )}
            {metadata?.last_update && (
              <div className="t-border t-mt-4 t-border-solid t-bg-surface-lighter-grey t-p-2 t-rounded t-space-y-2 t-border-neutral-0">
                <div className="t-text-body-sm t-text-text-60">Update</div>
                <ClampedTextWithTooltip
                  text={metadata?.last_update}
                  lineClampElementClass="t-line-clamp-2 t-text-body-sm t-text-text-30"
                  toolTipClass="t-w-64 t-word-break"
                />
              </div>
            )}
            <div
              className={classNames("t-mt-4 t-w-full t-flex-row-reverse", {
                "t-grid t-grid-cols-2 t-gap-2": cta_data?.length > 1,
              })}
            >
              {cta_data?.map((cta) => (
                <TicketCardBtn
                  key={cta.document_operation}
                  cta={cta}
                  openPaymentModal={openPaymentModal}
                  redirectToTicket={redirectToTicket}
                  isPaymentRequire={isPaymentRequire}
                  isGettingInvoices={isGettingInvoices}
                />
              ))}
            </div>
          </div>
        </ChatCard.Body>
      </ChatCard.Content>
      {paymentModal.isOpen && entityId && ticketInvoices?.length > 0 && (
        <CheckoutModal
          entityId={entityId!}
          type="invoices"
          open={paymentModal.isOpen}
          onClose={paymentModal.close}
          onInvoicePaid={onInvoicePaid}
          invoices={ticketInvoices}
          messageId={messageId}
          payerUserType={isForeignCA && "FCA"}
        />
      )}
    </div>
  );
};
